import React from "react"
import {
  SectionCard,
  StatusBadge,
  planInputStyle as inputStyle,
  planButtonStyle as buttonStyle
} from "./PlanPrimitives"

export default function PlanItemsSection({
  items,
  editRows,
  doctorOptions,
  isReadOnly,
  savingItemId,
  producingItemId,
  updateEditField,
  saveItemChanges,
  scheduleItem,
  openAppointment,
  registerProducedTreatment,
  updateItemStatus,
  formatMoney,
  formatDateTime
}) {
  const hasMultipleDoctors = Array.isArray(doctorOptions) && doctorOptions.length > 1
  const hasSingleDoctor = Array.isArray(doctorOptions) && doctorOptions.length === 1
  const singleDoctorName = hasSingleDoctor ? doctorOptions[0]?.name || "Doctor único" : ""

  return (
    <SectionCard title="Ítems del plan">
      <div style={{ display: "grid", gap: 14 }}>
        {items.map((item) => {
          const row = editRows[item.id] || {}
          const upperStatus = String(item.status || "").toUpperCase()

          return (
            <div
              key={item.id}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 18,
                padding: 16,
                background: "#ffffff",
                boxShadow: "0 8px 22px rgba(15,23,42,0.04)",
                display: "grid",
                gap: 14
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "center"
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 900,
                      color: "#0f172a",
                      letterSpacing: "-0.02em"
                    }}
                  >
                    {item.treatment?.name || "Tratamiento"}
                  </div>

                  <div
                    style={{
                      color: "#64748b",
                      fontSize: 13,
                      marginTop: 5,
                      lineHeight: 1.4
                    }}
                  >
                    Pieza: <strong>{item.tooth ?? "-"}</strong>
                    {" · "}
                    Superficie: <strong>{item.surface || "-"}</strong>
                    {" · "}
                    Condición: <strong>{item.condition || "-"}</strong>
                  </div>
                </div>

                <StatusBadge status={item.status} />
              </div>

              {hasSingleDoctor ? (
                <div
                  style={{
                    border: "1px solid #dbeafe",
                    background: "#eff6ff",
                    borderRadius: 14,
                    padding: 12,
                    color: "#1e3a8a",
                    fontSize: 13.5,
                    lineHeight: 1.4
                  }}
                >
                  Clínica con doctor único: <strong>{singleDoctorName}</strong>. No se muestra recomendación de doctor porque no aporta valor en este contexto.
                </div>
              ) : null}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: hasMultipleDoctors
                    ? "repeat(auto-fit, minmax(180px, 1fr))"
                    : "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12
                }}
              >
                <InfoMini label="Doctor asignado" value={item.assignedDoctor?.name || (hasSingleDoctor ? singleDoctorName : "-")} />

                {hasMultipleDoctors ? (
                  <InfoMini label="Doctor sugerido" value={item.suggestedDoctor?.name || "-"} />
                ) : null}

                <InfoMini label="Precio" value={formatMoney(item.price || 0)} />
                <InfoMini
                  label="Cita ligada"
                  value={
                    item.appointment?.startTime
                      ? formatDateTime(item.appointment.startTime)
                      : "Sin cita"
                  }
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: hasMultipleDoctors
                    ? "repeat(auto-fit, minmax(220px, 1fr))"
                    : "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: 12
                }}
              >
                <FieldBlock label="Asignar doctor">
                  <select
                    value={row.assignedDoctorId || ""}
                    onChange={(e) =>
                      updateEditField(item.id, "assignedDoctorId", e.target.value)
                    }
                    style={inputStyle}
                    disabled={isReadOnly || hasSingleDoctor}
                  >
                    {hasSingleDoctor ? (
                      <option value="">{singleDoctorName}</option>
                    ) : (
                      <>
                        <option value="">Sin asignar</option>
                        {doctorOptions.map((doctor) => (
                          <option key={`assigned-${doctor.id}`} value={doctor.id}>
                            {doctor.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </FieldBlock>

                {hasMultipleDoctors ? (
                  <FieldBlock label="Doctor sugerido">
                    <select
                      value={row.suggestedDoctorId || ""}
                      onChange={(e) =>
                        updateEditField(item.id, "suggestedDoctorId", e.target.value)
                      }
                      style={inputStyle}
                      disabled={isReadOnly}
                    >
                      <option value="">Sin sugerir</option>
                      {doctorOptions.map((doctor) => (
                        <option key={`suggested-${doctor.id}`} value={doctor.id}>
                          {doctor.name}
                        </option>
                      ))}
                    </select>
                  </FieldBlock>
                ) : null}

                <FieldBlock label="Precio">
                  <input
                    type="number"
                    value={row.price || ""}
                    onChange={(e) =>
                      updateEditField(item.id, "price", e.target.value)
                    }
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </FieldBlock>

                <FieldBlock label="Notas">
                  <input
                    value={row.notes || ""}
                    onChange={(e) =>
                      updateEditField(item.id, "notes", e.target.value)
                    }
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </FieldBlock>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap"
                }}
              >
                <button
                  onClick={() => saveItemChanges(item)}
                  disabled={savingItemId === item.id || isReadOnly}
                  style={{
                    ...buttonStyle,
                    background: "#ffffff",
                    cursor: savingItemId === item.id || isReadOnly ? "not-allowed" : "pointer"
                  }}
                >
                  {savingItemId === item.id ? "Guardando..." : "Guardar cambios"}
                </button>

                {upperStatus !== "DONE" ? (
                  <button
                    onClick={() => scheduleItem(item)}
                    disabled={isReadOnly}
                    style={{
                      ...buttonStyle,
                      background: "#eff6ff",
                      color: "#1d4ed8",
                      border: "1px solid #bfdbfe",
                      cursor: isReadOnly ? "not-allowed" : "pointer"
                    }}
                  >
                    Agendar
                  </button>
                ) : null}

                {item.appointment?.id ? (
                  <button
                    onClick={() =>
                      openAppointment({
                        appointmentId: item.appointment.id,
                        appointmentStart: item.appointment.startTime
                      })
                    }
                    style={{
                      ...buttonStyle,
                      background: "#f8fafc"
                    }}
                  >
                    Abrir cita
                  </button>
                ) : null}

                {upperStatus !== "DONE" ? (
                  <button
                    onClick={() =>
                      registerProducedTreatment({
                        ...item,
                        appointmentId: item.appointment?.id,
                        appointmentDoctorId: item.appointment?.doctorId
                      })
                    }
                    disabled={producingItemId === item.id || isReadOnly}
                    style={{
                      ...buttonStyle,
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      cursor: producingItemId === item.id || isReadOnly ? "not-allowed" : "pointer"
                    }}
                  >
                    {producingItemId === item.id ? "Registrando..." : "Registrar tratamiento"}
                  </button>
                ) : null}

                {upperStatus !== "CANCELLED" && upperStatus !== "DONE" ? (
                  <button
                    onClick={() => updateItemStatus(item, "CANCELLED")}
                    disabled={savingItemId === item.id || isReadOnly}
                    style={{
                      ...buttonStyle,
                      background: "#ffffff",
                      border: "1px solid #fecaca",
                      color: "#991b1b",
                      cursor: savingItemId === item.id || isReadOnly ? "not-allowed" : "pointer"
                    }}
                  >
                    Cancelar
                  </button>
                ) : null}

                {upperStatus === "CANCELLED" ? (
                  <button
                    onClick={() => updateItemStatus(item, "PENDING")}
                    disabled={savingItemId === item.id || isReadOnly}
                    style={{
                      ...buttonStyle,
                      background: "#ffffff",
                      border: "1px solid #bfdbfe",
                      color: "#1d4ed8",
                      cursor: savingItemId === item.id || isReadOnly ? "not-allowed" : "pointer"
                    }}
                  >
                    Reactivar
                  </button>
                ) : null}

                {upperStatus === "PENDING" ? (
                  <button
                    onClick={() => updateItemStatus(item, "IN_PROGRESS")}
                    disabled={savingItemId === item.id || isReadOnly}
                    style={{
                      ...buttonStyle,
                      background: "#ffffff",
                      cursor: savingItemId === item.id || isReadOnly ? "not-allowed" : "pointer"
                    }}
                  >
                    Marcar en proceso
                  </button>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}

function InfoMini({ label, value }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        borderRadius: 14,
        padding: 12
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: 0.3,
          marginBottom: 5
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontWeight: 800,
          color: "#0f172a",
          fontSize: 14,
          lineHeight: 1.35,
          wordBreak: "break-word"
        }}
      >
        {value}
      </div>
    </div>
  )
}

function FieldBlock({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          marginBottom: 6,
          display: "block",
          fontWeight: 800
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}